<template>
  <div>
    <template v-if="!nucleusUser()">
      <h1 class="page-header">No Access</h1>
      You do not have access to this page. Please contact HIA.
    </template>
    <div v-else class="home">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <clientNavSelector></clientNavSelector>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: `/${currentClientId}/requests` }">Requests</el-breadcrumb-item>
      <template v-if="!loading">
        <el-breadcrumb-item>Edit Coding
          Schedule Request for
          {{ getMonthYear(codingScheduleRequest.requestStartDate) }}
        </el-breadcrumb-item>
      </template>
    </el-breadcrumb>
    <hia-loading v-if="loading"></hia-loading>
    <div v-if="!loading">
      <el-row :gutter="40" style="margin-top: 40px;">
        <el-col :span="24">
          <CodingRequestProCoderDetails :loading="saving" :coders="coders" :preventZeroCoders="true" :startDate="parseISO(codingScheduleRequest.requestStartDate)" @add="addCoders" @changePatientTypes="changePatientTypes" @save="save" @delete="deleteCoders" @clearCoders="clearCoders">
          </CodingRequestProCoderDetails>

          <el-form size="mini" style="margin: 20px 10px 0px 10px;">
            <el-form-item label="Additional Notes">
              <el-input type="textarea" rows="3" v-model="codingScheduleRequest.notes" @change="putRequest"></el-input>
            </el-form-item>
          </el-form>

        </el-col>
      </el-row>
    </div>
    </div>
  </div>
</template>

<script>
  import { parseISO } from 'date-fns'
  import { mapState, mapActions } from 'vuex'
  import CodingRequestProCoderDetails from './CodingRequestProCoderDetails'
  import HiaAuthorization from '@/mixins/hiaAuthorization'

  export default {
    mixins: [HiaAuthorization],
    data: function () {
      return {
        parseISO: parseISO,
        loading: true,
        saving: false,
        coders: [],
        saveDisabled: true,
        addRow: {
          numberOfCoders: 1,
          patientTypes: [],
          scheduledDays: [],
          rules: {
            patientTypes: [
              { required: true, message: 'Please Select Patient Types', trigger: 'change' }
            ],
            scheduledDays: [
              { required: true, message: 'Please Select Scheduled Days', trigger: 'change' }
            ],
            numberOfCoders: [
              { required: true, message: 'Please Set Number of Coders', trigger: 'change' }
            ]
          }
        },
        patientTypes: [
          { key: 'Inpatient' },
          { key: 'Ambulatory Surgery' },
          { key: 'Ancilliary' },
          { key: 'Emergency Department' },
          { key: 'Other Outpatients with Procedures' }
        ]
      }
    },
    name: 'NewCodingRequest',
    methods: {
      ...mapActions('requests/', ['GET_CODING_SCHEDULE_REQUEST', 'POST_PRO_CODERS', 'DELETE_PRO_CODERS', 'PUT_PRO_CODERS', 'GET_SPECIALTIES', 'PUT_CODING_SCHEDULE_REQUEST']),
      handleCoderSaveError(error, reloadAfterError) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Unable to save coding schedule request.'
        this.$message({
          type: 'error',
          message: message,
          duration: 5000
        })
        if (reloadAfterError) {
          this.saving = false
          this.initialize()
        }
      },
      initialize() {
        if (!this.nucleusUser()) {
          this.loading = false
          return
        }
        this.loading = true
        Promise.all([this.GET_CODING_SCHEDULE_REQUEST(this.$route.params.id), this.GET_SPECIALTIES()]).then(() => {
          this.setNestedCoders()
          this.loading = false
        })
      },
      putRequest() {
        this.PUT_CODING_SCHEDULE_REQUEST(this.codingScheduleRequest).then(() => {
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          }, error => {
            console.log(error)
            this.$message({
              message: 'Error Saving Coding Schedule Request',
              type: 'error'
            })
          })
        })
      },
      setNestedCoders() {
        let scheduleRequestsDetailsCodingPro = this.codingScheduleRequest.scheduleRequestsDetailsCodingPro
        let scheduleRequestsSpecialtiesCodingPro = this.codingScheduleRequest.scheduleRequestsSpecialtiesCodingPro
        let coderDisplayNumber = 1
        this.coders = []
        for (let i = 0; i < scheduleRequestsDetailsCodingPro.length; i++) {
          let row = scheduleRequestsDetailsCodingPro[i]
          let rowDate = parseISO(row.requestDate).getDate()
          let existingIndex = this.coders.findIndex(x => x.coderNumber === row.coderNumber)
          if (existingIndex > -1) {
            this.coders[existingIndex].scheduledDays.push({ date: rowDate, id: row.id })
          } else {
            this.coders.push({
              coder: `Coder #${coderDisplayNumber}`,
              coderNumber: row.coderNumber,
              scheduledDays: [{ date: rowDate, id: row.id }],
              // need to set the full list of specialties and selected flags for the persisted ones
              specialties: this.specialties.map(x => {
                let persistedSpecialty = scheduleRequestsSpecialtiesCodingPro.find(y => y.specialtyId === x.id && y.coderNumber === row.coderNumber)
                if (persistedSpecialty) {
                  return {
                    isClinic: persistedSpecialty.isClinic,
                    isHospital: persistedSpecialty.isHospital,
                    isDx: persistedSpecialty.isDx,
                    isEM: persistedSpecialty.isEM,
                    isSurgCpt: persistedSpecialty.isSurgCpt,
                    selected: true,
                    key: x.specialty,
                    specialtyId: x.id,
                    description: persistedSpecialty.description,
                    id: persistedSpecialty.id
                  }
                } else {
                  return {
                    isClinic: false,
                    isHospital: false,
                    isDx: false,
                    isEM: false,
                    isSurgCpt: false,
                    selected: false,
                    key: x.specialty,
                    description: '',
                    specialtyId: x.id
                  }
                }
              }),
              showSpecialtiesModal: false,
              showCalendar: false,
              calendarSaveDisabled: true
            })
            coderDisplayNumber++
          }
        }
      },
      save(coder) {
        this.saving = true
        let payload = this.getFlattenedCodersList([coder])
        this.PUT_PRO_CODERS(payload).then(() => {
          this.setNestedCoders() // need to reset with the latest data from the server
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        }).catch(error => {
          this.handleCoderSaveError(error, true)
        })
      },
      changePatientTypes(coder) {
        this.saving = true
        let payload = this.getFlattenedCodersList([coder])
        this.PUT_PRO_CODERS(payload).then(() => {
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        }).catch(error => {
          this.handleCoderSaveError(error, true)
        })
      },
      addCoders(newCoders) {
        this.saving = true
        let payload = this.getFlattenedCodersList(newCoders)
        this.POST_PRO_CODERS(payload).then(() => {
          this.setNestedCoders()
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        })
      },
      deleteCoders(coder) {
        this.$confirm(`Permanently delete ${coder.coder}?`, 'Delete coder', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.saving = true
          let wrapper = {
            requestId: this.codingScheduleRequest.requestId,
            scheduleRequestsDetailsCodingPro: this.codingScheduleRequest.scheduleRequestsDetailsCodingPro.filter(x => x.coderNumber === coder.coderNumber),
            scheduleRequestsSpecialtiesCodingPro: this.codingScheduleRequest.scheduleRequestsSpecialtiesCodingPro.filter(x => x.coderNumber === coder.coderNumber)
          }
          this.DELETE_PRO_CODERS(wrapper).then(() => {
            let i = this.coders.findIndex(x => x.coderNumber === coder.coderNumber)
            this.coders.splice(i, 1)
            this.coders = this.coders.map((x, i) => {
              x.coder = `Coder #${i + 1}`
              return x
            })
            this.saving = false
            this.$message({
              message: 'Coding Schedule Request Saved',
              type: 'success'
            })
          }).catch(error => {
            this.handleCoderSaveError(error, true)
          })
        })
      },
      getFlattenedCodersList(coders) {
        let wrapper = {
          requestId: this.codingScheduleRequest.requestId,
          scheduleRequestsDetailsCodingPro: [],
          scheduleRequestsSpecialtiesCodingPro: []
        }

        let selectedMonth = parseISO(this.codingScheduleRequest.requestStartDate)
        let flattenedCodersList = []
        let flattenedSpecialtiesList = []

        for (let i = 0; i < coders.length; i++) {
          for (let j = 0; j < coders[i].scheduledDays.length; j++) {
            flattenedCodersList.push({
              coderNumber: coders[i].coderNumber,
              requestDate: `${selectedMonth.getFullYear()}-${selectedMonth.getMonth() + 1}-${coders[i].scheduledDays[j].date}`,
              requestId: this.codingScheduleRequest.requestId,
              id: coders[i].scheduledDays[j].id
            })
          }

          let selectedSpecialties = coders[i].specialties.filter(x => x.selected)
          for (let j = 0; j < selectedSpecialties.length; j++) {
            flattenedSpecialtiesList.push({
              coderNumber: coders[i].coderNumber,
              specialtyId: selectedSpecialties[j].specialtyId,
              isDx: selectedSpecialties[j].isDx,
              isEM: selectedSpecialties[j].isEM,
              isSurgCpt: selectedSpecialties[j].isSurgCpt,
              isHospital: selectedSpecialties[j].isHospital,
              isClinic: selectedSpecialties[j].isClinic,
              requestId: this.codingScheduleRequest.requestId,
              description: selectedSpecialties[j].description,
              id: selectedSpecialties[j].id
            })
          }
        }

        wrapper.scheduleRequestsDetailsCodingPro = flattenedCodersList
        wrapper.scheduleRequestsSpecialtiesCodingPro = flattenedSpecialtiesList

        return wrapper
      },
      getSelectedMonth() {
        return this.getMonthsDropdown().find(x => x.key === this.codingScheduleRequest.month)
      },
      getMonthYear(isoDate) {
        let d = parseISO(isoDate)
        return `${d.toLocaleString('en-us', { month: 'long' })} ${d.getFullYear()}`
      },
      clearCoders() {
        this.$confirm(`Permanently delete all coders?`, 'Clear coders', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.saving = true
          this.DELETE_PRO_CODERS(this.codingScheduleRequest).then(() => {
            this.coders = []
            this.saving = false
            this.$message({
              message: 'Coding Schedule Request Saved',
              type: 'success'
            })
          }).catch(error => {
            this.handleCoderSaveError(error, true)
          })
        })
      }
    },
    created: function () {
      this.initialize()
    },
    computed: {
      ...mapState('user/', ['currentClient', 'currentClientId']),
      ...mapState('requests/', ['codingScheduleRequest', 'specialties'])
    },
    components: {
      CodingRequestProCoderDetails
    }
  }
</script>

<style scoped>
.sticky {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
}

.saveWarning {
  margin-top: 10px;
  padding: 5px;
  background-color: #fdf6ec;
  border-radius: 3px;
}
</style>
